import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './LazyImage.module.css';
import fallbackImg from '../../assets/images/ifillip-logo.png';

// 1×1 transparent GIF — used as initial src before isInView flips
const BLANK_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * LazyImage — lazy-loaded image with optional progressive (blur-up) loading.
 *
 * Features:
 *  - Native lazy loading + IntersectionObserver fallback
 *  - Shimmer placeholder while loading
 *  - Optional low-quality preview that blurs in first, then sharpens to full
 *  - Fallback image on error (accepts string OR { src } object)
 *  - Forwards all <img> props (srcSet, sizes, etc.)
 *
 * Usage:
 *   <LazyImage src={hero} alt="Hero" />
 *   <LazyImage src={hero} lowSrc={heroTiny} alt="Hero" />
 *   <LazyImage {...ifillip_school_students} alt="Hero" />   // spread from controller
 */
const LazyImage = ({
  src,
  lowSrc,
  getLowSrc,
  alt = '',
  className = '',
  wrapperClassName = '',
  fallback = fallbackImg,
  width,
  height,
  onLoad,
  onError,
  ...rest
}) => {
  const [isHighLoaded, setIsHighLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  // Resolve low-quality source
  const lowQualitySrc = useMemo(() => {
    if (lowSrc) return lowSrc;
    if (getLowSrc && src) {
      try {
        return getLowSrc(src);
      } catch {
        return null;
      }
    }
    return null;
  }, [src, lowSrc, getLowSrc]);

  // Normalize fallback — accept both "url-string" and { src: "url" }
  const resolvedFallback =
    typeof fallback === 'string' ? fallback : fallback?.src;

  // Native lazy loading + IntersectionObserver fallback
  useEffect(() => {
    const node = imgRef.current;
    if (!node) return;

    if ('loading' in HTMLImageElement.prototype) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Reset states when src changes
  useEffect(() => {
    setIsHighLoaded(false);
    setHasError(false);
  }, [src]);

  const handleLoad = (e) => {
    setIsHighLoaded(true);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    setHasError(true);
    setIsHighLoaded(true);
    if (onError) onError(e);
  };

  const finalHighSrc = hasError ? resolvedFallback : src;
  const hasLowPreview = Boolean(lowQualitySrc) && !hasError;

  return (
    <div
      className={`${styles.wrapper} ${wrapperClassName}`}
      style={{ width, height }}
    >
      {/* Shimmer: only when no low-quality preview is available */}
      {!isHighLoaded && !hasLowPreview && (
        <div className={styles.placeholder} aria-hidden="true" />
      )}

      {/* Stage 1: blurred preview */}
      {hasLowPreview && isInView && (
        <img
          src={lowQualitySrc}
          alt=""
          aria-hidden="true"
          decoding="async"
          className={`${styles.image} ${styles.lowQuality} ${
            isHighLoaded ? styles.fadeOut : ''
          }`}
        />
      )}

      {/* Stage 2: full-quality image, fades in on top */}
      <img
        ref={imgRef}
        src={isInView ? finalHighSrc : BLANK_PIXEL}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        className={`${styles.image} ${styles.highQuality} ${
          isHighLoaded ? styles.loaded : ''
        } ${className}`}
        {...rest}
      />
    </div>
  );
};

export default LazyImage;